import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Header from "./components/Header";

import Home from "./pages/Home";
import ProductsServices from "./pages/ProductsServices";
import MagInsight from "./pages/MagInsight";
import Contact from "./pages/Contact";
import CaseStudies from "./pages/CaseStudies";
import WhoWeAre from "./pages/WhoWeAre";
import TermsOfService from "./pages/TermsOfService";
import VisitorWaiver from "./pages/VisitorWaiver";
import MagLab from "./pages/MagLab";
import Careers from "./pages/Careers";
import EquipmentIndex from "./pages/EquipmentIndex";
import EquipmentDetail from "./pages/EquipmentDetail";
import EquipmentCompare from "./pages/EquipmentCompare";
import ApplicationDetail from "./pages/ApplicationDetail";
import NotFound from "./pages/NotFound";
import Footer from "./components/Footer";

// First load (prerender/hydrate) is a static wrapper so captured HTML matches React.
// Client-side navigations use motion for the fade transition.
function PageFade({ children, animateEnter }) {
  if (!animateEnter) {
    return (
      <div className="min-h-[60vh]" data-page-ready="true">
        {children}
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="min-h-[60vh]"
      data-page-ready="true"
    >
      {children}
    </motion.div>
  );
}

// Routes with fade + scroll-to-top on exit
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const id = decodeURIComponent(hash.replace("#", ""));
    const timeout = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
    return () => window.clearTimeout(timeout);
  }, [pathname, hash]);

  return null;
}

function LegacyCatalogRedirect() {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/equipment(?=\/|$)/, "/catalog") + search + hash;
  return <Navigate to={next} replace />;
}

function RoutesWithFade() {
  const location = useLocation();
  const firstPathRef = useRef(location.pathname);
  const animateEnter = location.pathname !== firstPathRef.current;

  // Send GA4 page_view on route change (if gtag is present).
  // React 19 commits <title> before effects, so document.title should match the new page.
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[GA page_view]", {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
      });
    }
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <AnimatePresence
      mode="wait"
      initial={false}
      onExitComplete={() => {
        // Ensure top of page before new fade-in
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        requestAnimationFrame(() => window.scrollTo(0, 0));
      }}
    >
      <Routes location={location} key={location.pathname}>
        {/* Keep these path values in sync with src/routes.js (routes.drift.test.js). */}
        <Route path="/" element={<PageFade animateEnter={animateEnter}><Home /></PageFade>} />
        <Route path="/who-we-are" element={<PageFade animateEnter={animateEnter}><WhoWeAre /></PageFade>} />
        <Route
          path="/products-services"
          element={<PageFade animateEnter={animateEnter}><ProductsServices /></PageFade>}
        />
        <Route path="/maginsight" element={<PageFade animateEnter={animateEnter}><MagInsight /></PageFade>} />
        <Route path="/contact" element={<PageFade animateEnter={animateEnter}><Contact /></PageFade>} />
        <Route
          path="/case-studies"
          element={<PageFade animateEnter={animateEnter}><CaseStudies /></PageFade>}
        />
        <Route
          path="/terms-of-service"
          element={<PageFade animateEnter={animateEnter}><TermsOfService /></PageFade>}
        />
        <Route
          path="/docs/visitor-waiver"
          element={<PageFade animateEnter={animateEnter}><VisitorWaiver /></PageFade>}
        />
        <Route path="/mag-lab" element={<PageFade animateEnter={animateEnter}><MagLab /></PageFade>} />
        <Route path="/careers" element={<PageFade animateEnter={animateEnter}><Careers /></PageFade>} />
        <Route path="/amr-integration" element={<Navigate to="/" replace />} />
        <Route path="/agv-integration" element={<Navigate to="/" replace />} />
        <Route path="/amr-vs-agv" element={<Navigate to="/" replace />} />
        <Route path="/michigan-amr-agv-integrator" element={<Navigate to="/" replace />} />
        <Route path="/brownfield-automation" element={<Navigate to="/" replace />} />
        <Route path="/equipment/*" element={<LegacyCatalogRedirect />} />
        <Route path="/catalog" element={<PageFade animateEnter={animateEnter}><EquipmentIndex /></PageFade>} />
        <Route
          path="/catalog/compare/:pair"
          element={<PageFade animateEnter={animateEnter}><EquipmentCompare /></PageFade>}
        />
        <Route
          path="/catalog/:slug"
          element={<PageFade animateEnter={animateEnter}><EquipmentDetail /></PageFade>}
        />
        <Route
          path="/applications/:slug"
          element={<PageFade animateEnter={animateEnter}><ApplicationDetail /></PageFade>}
        />
        <Route path="*" element={<PageFade animateEnter={animateEnter}><NotFound /></PageFade>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <Header />
      <ScrollToHash />
      <main id="main-content" className="bg-white">
        <RoutesWithFade />
      </main>
      <Footer />
    </Router>
  );
}

export default App;
